import React, { useState, useEffect, useRef } from 'react';

// Import de la Lightbox et de ses styles de base
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

// Optionnel: Import des plugins (miniatures, zoom) et leurs styles
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// Import du CSS spécifique à notre galerie
import './Gallery.css';

// --- Données pour les images de la galerie ---
const imageFiles = [
  // Images existantes
   // Note: 's4.jpeg' est listé deux fois, conservé tel quel.
   's6.jpeg',

  // Nouvelles images de a1.jpeg à a16.jpeg
  'a1.jpeg', 'a2.jpeg', 'a3.jpeg', 'a4.jpeg', 'a5.jpeg', 'a6.jpeg', 'a7.jpeg', 'a8.jpeg',
  'a9.jpeg', 'a10.jpeg', 'a11.jpeg', 'a12.jpeg', 'a13.jpeg', 'a14.jpeg', 'a15.jpeg', 'a16.jpeg',

  // NOUVELLES IMAGES : de y1.jpg à y15.jpg
  'y1.webp', 'y2.webp', 'y3.jpg', 'y4.jpg', 'y5.jpg', 'y6.jpg', 'y7.jpg', 'y8.jpg',
  'y9.jpg', 'y10.jpg', 'y11.jpg', 'y12.webp', 'y13.webp', 'y14.jpg', 'y15.jpg'
];

const galleryImages = imageFiles.map((file, index) => ({
  id: `gallery-img-${index + 1}-${file.split('.')[0]}`,
  src: `/images/${file}`,
  alt: `Galerie Studio Ambre - ${file}`
}));

// Configuration pour le nombre de lignes
const INITIAL_ROWS_TO_DISPLAY = 2; // Nombre de lignes à afficher initialement
const INCREMENT_ROWS_TO_DISPLAY = 1; // Nombre de lignes à ajouter avec "Voir plus"
const FALLBACK_ITEMS_PER_ROW = 3; // Utilisé si la détection échoue ou avant le premier calcul

function Gallery() {
  const [openLightbox, setOpenLightbox] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const gridRef = useRef(null);
  const [itemsPerRow, setItemsPerRow] = useState(FALLBACK_ITEMS_PER_ROW);

  // Calcul du nombre d'images initiales et d'incrément basé sur itemsPerRow
  const dynamicInitialCount = Math.min(itemsPerRow * INITIAL_ROWS_TO_DISPLAY, galleryImages.length);
  const dynamicIncrementCount = itemsPerRow * INCREMENT_ROWS_TO_DISPLAY;

  const [visibleCount, setVisibleCount] = useState(dynamicInitialCount);

  // Effet pour calculer itemsPerRow en fonction de la taille de la grille et des CSS
  useEffect(() => {
    const calculateItemsPerRow = () => {
      if (gridRef.current) {
        const gridNode = gridRef.current;
        const gridComputedStyle = window.getComputedStyle(gridNode);
        const gridTemplateColumns = gridComputedStyle.getPropertyValue("grid-template-columns");
        
        let calculatedItems = FALLBACK_ITEMS_PER_ROW;

        if (window.innerWidth <= 480) {
            // Pour les mobiles, votre CSS est 'repeat(2, 1fr)', donc 2 colonnes.
            // On peut essayer de le confirmer en comptant les éléments de la première ligne.
            if (gridNode.children.length > 0) {
              const firstItemOffsetTop = (gridNode.children[0]).offsetTop;
              let count = 0;
              for (let i = 0; i < gridNode.children.length; i++) {
                  if ((gridNode.children[i]).offsetTop === firstItemOffsetTop) {
                      count++;
                  } else {
                      break; // On passe à la ligne suivante
                  }
              }
              calculatedItems = count > 0 ? count : 2; // Au cas où, fallback à 2 pour mobile
            } else {
                calculatedItems = 2; // Si pas encore d'enfants, on se base sur le CSS
            }
        } else {
            // Pour les autres tailles, on compte les colonnes définies par le style calculé
            // ex: "250px 250px 250px" -> 3 colonnes
            const columnStrings = gridTemplateColumns.split(/\s+/).filter(s => s && s !== '0px' && s !== 'none');
            if (columnStrings.length > 0) {
                calculatedItems = columnStrings.length;
            }
        }
        setItemsPerRow(calculatedItems);
      }
    };

    // Calcul initial (peut nécessiter un petit délai pour que la grille soit pleinement rendue)
    const timer = setTimeout(calculateItemsPerRow, 50);
    
    // Recalculer au redimensionnement (avec debounce pour la performance)
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateItemsPerRow, 150);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Exécuter une seule fois à l'initialisation pour mettre en place les écouteurs

  // Effet pour ajuster visibleCount quand itemsPerRow ou la configuration change
  useEffect(() => {
    // Recalculer dynamicInitialCount ici car il dépend de itemsPerRow qui est un état
    const newInitialCount = Math.min(itemsPerRow * INITIAL_ROWS_TO_DISPLAY, galleryImages.length);
    setVisibleCount(newInitialCount);
  }, [itemsPerRow, galleryImages.length]);


  const handleImageClick = (indexInSlicedArray) => {
    const globalIndex = galleryImages.findIndex(img => img.id === galleryImages.slice(0, visibleCount)[indexInSlicedArray].id);
    setCurrentImageIndex(globalIndex);
    setOpenLightbox(true);
  };

  const handleShowMore = () => {
    setVisibleCount(prevCount => Math.min(prevCount + dynamicIncrementCount, galleryImages.length));
  };

  const handleShowLess = () => {
    const newVisibleCount = Math.min(itemsPerRow * INITIAL_ROWS_TO_DISPLAY, galleryImages.length);
    setVisibleCount(newVisibleCount);
    const galleryElement = document.getElementById('gallery');
    if (galleryElement) {
      galleryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const lightboxSlides = galleryImages.map(img => ({ src: img.src, alt: img.alt }));

  const showMoreButtonVisible = visibleCount < galleryImages.length;
  // dynamicInitialCount doit être utilisé ici pour la comparaison
  const currentDynamicInitialCount = Math.min(itemsPerRow * INITIAL_ROWS_TO_DISPLAY, galleryImages.length);
  const showLessButtonVisible = visibleCount > currentDynamicInitialCount && galleryImages.length > currentDynamicInitialCount;


  return (
    <section id="gallery" className="gallery-section">
      <div className="container">
        <h2 className="section-title">Le Salon en Images</h2>

        <div className="gallery-grid" ref={gridRef}>
          {galleryImages.slice(0, visibleCount).map((image, index) => (
            <div
              key={image.id}
              className="gallery-item"
              onClick={() => handleImageClick(index)}
              role="button"
              tabIndex={0}
              aria-label={`Voir ${image.alt}`}
              onKeyDown={(e) => e.key === 'Enter' && handleImageClick(index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="gallery-image"
                loading="lazy"
              />
              <div className="gallery-item-overlay">
                <span className="overlay-icon">+</span>
              </div>
            </div>
          ))}
        </div>

        {(showMoreButtonVisible || showLessButtonVisible) && (
          <div className="gallery-controls">
            {showMoreButtonVisible && (
              <button onClick={handleShowMore} className="gallery-button">
                Voir plus
              </button>
            )}
            {showLessButtonVisible && (
              <button onClick={handleShowLess} className="gallery-button gallery-button-secondary">
                Voir moins
              </button>
            )}
          </div>
        )}
      </div>

      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        index={currentImageIndex}
        slides={lightboxSlides}
        plugins={[Thumbnails, Zoom]}
        zoom={{
          maxZoomPixelRatio: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
        }}
        thumbnails={{
          border: 0,
          padding: 0,
          gap: 8,
          imageFit: "cover",
          height: 60,
        }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, .9)" },
          thumbnailsContainer: { backgroundColor: "rgba(0,0,0, .7)" },
        }}
      />
    </section>
  );
}

export default Gallery;